/**
 * CSV Parser for Banpo Map Data
 * Parses AutoCAD exported CSV with layer-based grouping
 */

import { log, LogCategory } from './logger'
import { IDGenerator } from './idGenerator'
import { isLegacyId } from '../constants/idPrefixes'

export interface BanpoRow {
  x: number
  y: number
  z: number
  layer: string
  paperSpace: string
  subClasses: string
  linetype: string
  entityHandle: string
  text?: string
  style?: string
}

export interface GroupedEntity {
  layer: string
  entityHandle: string
  points: BanpoRow[]
  isPolygon: boolean
  isClosed: boolean
  isPoint?: boolean // Flag for point entities (CCTV, sensors, etc.)
  id?: string // Standardized ID
  originalId?: string // Original entity handle
  objectType?: string // Mapped object type (e.g., 'p-parking-basic')
}

export interface ParseOptions {
  idGenerator?: IDGenerator
  preserveOriginalId?: boolean
  floorInfo?: string
  layerTypeMapping?: Map<string, string> // layer -> object type mapping
}

/**
 * Parse CSV text into structured data
 */
export function parseBanpoCSV(csvText: string): BanpoRow[] {
  const lines = csvText.trim().split('\n')

  // Skip header row
  const dataLines = lines.slice(1)

  const rows: BanpoRow[] = []

  dataLines.forEach((line, index) => {
    if (!line.trim()) return

    // Robust CSV regex matcher
    // Matches:
    // 1. Quoted text: "..." (capturing group 1)
    // 2. Simple text: ... (capturing group 2)
    const matches = line.matchAll(/(?:^|,)(?:"([^"]*)"|([^,]*))/g)
    const cols: string[] = []

    for (const match of matches) {
      // If group 1 exists, it's a quoted value (quotes already stripped by regex group)
      // If group 2 exists, it's a unquoted value
      // Use undefined check because empty string is valid
      const val = match[1] !== undefined ? match[1] : match[2]
      cols.push(val)
    }

    // Parse coordinates
    const x = parseFloat(cols[0])
    const y = parseFloat(cols[1])
    const z = parseFloat(cols[2])

    // Skip invalid rows
    if (isNaN(x) || isNaN(y)) {
      log.warn(LogCategory.CSV, `Skipping invalid row ${index + 2}`, { line })
      return
    }

    const row: BanpoRow = {
      x,
      y,
      z,
      layer: cols[3]?.trim() || '',
      paperSpace: cols[4]?.trim() || '',
      subClasses: cols[5]?.trim() || '',
      linetype: cols[6]?.trim() || '',
      entityHandle: cols[7]?.trim() || '',
      text: cols[8]?.trim() || undefined,
      style: cols[9]?.trim() || undefined,
    }

    rows.push(row)
  })

  log.info(LogCategory.CSV, `Parsed ${rows.length} rows from CSV`)
  return rows
}

/**
 * Group rows by entity handle (for polygons)
 */
export function groupByEntity(rows: BanpoRow[], options?: ParseOptions): GroupedEntity[] {
  const groups = new Map<string, BanpoRow[]>()

  // Group by layer + entityHandle
  rows.forEach(row => {
    const key = `${row.layer}_${row.entityHandle}`
    if (!groups.has(key)) {
      groups.set(key, [])
    }
    groups.get(key)!.push(row)
  })

  const entities: GroupedEntity[] = []

  const EPS = 1e-6
  groups.forEach((points) => {
    if (points.length === 0) return

    const layer = points[0].layer
    const entityHandle = points[0].entityHandle

    // Determine if this is a closed polygon (epsilon for float comparison)
    const first = points[0]
    const last = points[points.length - 1]
    const isClosed = points.length >= 3 &&
      Math.abs(first.x - last.x) < EPS &&
      Math.abs(first.y - last.y) < EPS

    // All closed shapes render as polygons (CCTV, COLUMN, 주차면 등 사용자 선택 레이어 포함)
    const isPolygon = isClosed

    // Detect point entities
    // Point entities are those with single coordinate or all points at same position
    let isPoint = false
    if (points.length === 1) {
      isPoint = true
    } else {
      // Check if all points are at the same position (collapsed to a point)
      const allSamePosition = points.every(p =>
        Math.abs(p.x - first.x) < EPS &&
        Math.abs(p.y - first.y) < EPS
      )
      if (allSamePosition) {
        isPoint = true
      }
    }

    // Also check layer names that commonly represent point entities
    const pointLayerKeywords = ['cctv', 'sensor', 'bell', 'emergency', 'occupancy']
    const layerLower = layer.toLowerCase()
    if (pointLayerKeywords.some(keyword => layerLower.includes(keyword))) {
      // For layers like CCTV, emergency bell, etc., treat them as points even if they have a polygon shape
      // This allows them to be mapped to icon-based object types
      isPoint = true
    }

    // Generate standardized ID if ID generator is provided
    let id = entityHandle
    let originalId = entityHandle
    let objectType = layer // default to layer name

    if (options?.idGenerator) {
      // Get object type from layer mapping if provided
      if (options.layerTypeMapping) {
        objectType = options.layerTypeMapping.get(layer) || layer
      }

      // Generate new ID unless preserving original and it's not a legacy ID
      if (!options.preserveOriginalId || isLegacyId(entityHandle)) {
        id = options.idGenerator.generateId(objectType, options.floorInfo)
      }
    }

    entities.push({
      layer,
      entityHandle,
      points,
      isPolygon,
      isClosed,
      isPoint,
      id,
      originalId,
      objectType
    })
  })

  log.info(LogCategory.CSV, `Grouped into ${entities.length} entities`)
  return entities
}

/**
 * Get layer statistics
 */
export function getLayerStats(rows: BanpoRow[]): Map<string, number> {
  const stats = new Map<string, number>()

  rows.forEach(row => {
    const count = stats.get(row.layer) || 0
    stats.set(row.layer, count + 1)
  })

  return stats
}

/**
 * Calculate bounds from rows
 */
export function calculateBounds(rows: BanpoRow[]): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  rows.forEach(row => {
    minX = Math.min(minX, row.x)
    minY = Math.min(minY, row.y)
    maxX = Math.max(maxX, row.x)
    maxY = Math.max(maxY, row.y)
  })

  return { minX, minY, maxX, maxY }
}

/**
 * Transform coordinates from AutoCAD space to canvas space
 */
export function transformCoordinates(
  x: number,
  y: number,
  options: {
    minX: number
    minY: number
    maxX?: number
    maxY?: number
    scale?: number
    flipY?: boolean
    flipX?: boolean
  }
): { x: number; y: number } {
  const {
    minX,
    minY,
    maxX,
    maxY,
    scale = 1.0,     // 1:1 scale (preserve original units)
    flipY = true,    // ALWAYS flip Y to match preview canvas (entrance at top-left)
    flipX = false    // No X flip by default
  } = options

  // X transformation - just offset and scale (no flip)
  let transformedX = (x - minX) * scale

  // Y transformation - matches preview canvas exactly
  // For negative Y values (AutoCAD), y - minY gives positive values
  // e.g., y=-41950, minY=-50700 → -41950-(-50700) = 8750
  let transformedY = (y - minY) * scale

  // Apply Y flip using canvas height (same as preview: canvas.height - value)
  // This flips so Y=0 is at top (like canvas coordinates)
  if (flipY && maxY !== undefined) {
    const height = (maxY - minY) * scale
    const originalY = transformedY
    transformedY = height - transformedY
    log.debug(LogCategory.CSV, 'Y-Flip', { from: originalY.toFixed(2), to: transformedY.toFixed(2), height: height.toFixed(2) })
  } else {
    log.warn(LogCategory.CSV, 'Y-Flip SKIPPED', { flipY, maxY })
  }

  // Apply horizontal flip if explicitly needed
  if (flipX && maxX !== undefined) {
    const width = (maxX - minX) * scale
    transformedX = width - transformedX
  }

  return { x: transformedX, y: transformedY }
}

/**
 * Convert coordinate array to SVG path string
 */
export function coordsToSVGPath(coords: { x: number; y: number }[]): string {
  if (coords.length === 0) return ''

  const pathParts = coords.map((point, i) =>
    i === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`
  )

  return pathParts.join(' ') + ' Z'  // Z = close path
}
